import { Link } from 'react-router-dom';
import {
  useBookings,
  outcomeOf,
  attendeeOutcome,
  whoIsGoing,
  type Attendee,
  type BookedLesson,
} from '../api/use-bookings.js';
import { lessonLink, athleteLessonLink } from '../api/lesson-link.js';
import { firstLine } from '../api/first-line.js';
import { CoachingRows } from '../components/coaching-rows.js';
import { YourAthletes } from '../components/your-athletes.js';
import { HomeTeams } from '../components/home-teams.js';
import { usePeople } from '../api/use-people.js';
import { WaitNote } from '../components/wait-note.js';
import { useAssessmentTimes } from '../api/use-assessment-times.js';

/**
 * Home.
 *
 * WHAT THIS SCREEN USED TO DO. Five panels of hardcoded text: "No lessons
 * booked", "Awaiting the API" twice, "Nothing yet" twice. It shipped that way
 * to production, so a parent holding two booked lessons was told they had
 * none, and two panels reported our build status to a customer.
 *
 * Ruled 25 September, and it follows design-principles.md §1: never fill
 * space — a panel with nothing to say says nothing. So the panels that could
 * not answer their own question are gone rather than filled with a placeholder,
 * and each returns when the thing behind it exists:
 *
 *   Package credits      when credits are surfaced to a parent at all
 *   Unread messages      with messaging
 *
 * LATEST LESSON (mockup of 11 October) is the recent practice plan come back,
 * now there is a lesson page to open: one row per person, their most recent
 * lesson that took place, with the first line of the plan. Nothing when nobody
 * has had one. NEXT LESSON carries the first line of its plan too, since the
 * plan is read before the lesson (ruled 13 October).
 *
 * Account balance is not coming back. The ledger was descoped, so there is no
 * balance to show and there never will be one on this design.
 *
 * YOUR ATHLETES (Performance Signature stage 4b, approved 25 October) sits
 * between them: one row per child, their codes or their next lesson, opening
 * their page.
 *
 * MY TEAMS (Teams stage 3, board 11, approved 26 October) sits above it: a
 * pill per team the person coaches or plays on, and per child's team.
 *
 * NEXT CLINIC (Matthew, 31 October) is its own section, only when one is booked,
 * with the clinic's title leading instead of the time. Whichever of it and Next
 * lesson comes first in time is on top.
 * A family with a clinic and no lesson is not told "Nothing booked", so Next
 * lesson says nothing for them.
 *
 * What is left is the one question a home screen is actually for — and, for
 * an MPC coach, his own tools above it (moved here from Account, 11 October).
 */
export function HomeRoute() {
  const times = useAssessmentTimes();
  const { state } = useBookings();
  // Asked for at once, beside the lessons - not after them (Matthew, 25
  // October: the athletes drew late because they waited for the lessons).
  const { state: people } = usePeople();

  const lessons = state.status === 'ready' ? state.lessons : [];
  const next = nextUp(lessons.filter((lesson) => !lesson.is_clinic));
  const nextClinic = nextUp(lessons.filter((lesson) => lesson.is_clinic));
  const clinicFirst =
    nextClinic !== undefined && (!next || Date.parse(nextClinic.slot_start) < Date.parse(next.slot_start));

  const clinicPanel = nextClinic ? (
    <section className="panel">
      <h2>Next clinic</h2>
      <Link className="hour-row" to={lessonLink(nextClinic)}>
        <span className="hour-row-text">
          <span className="name">{nextClinic.product_name}</span>
          <span className="detail">
            {[when(nextClinic.slot_start), who(nextClinic), `Coach ${nextClinic.coach_name}`]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <PlanLines lesson={nextClinic} />
        </span>
        <span className="chevron" aria-hidden="true">
          ›
        </span>
      </Link>
    </section>
  ) : null;

  return (
    <>
      <CoachingRows />

      {/* One per family, however many on the team still need one (Matthew,
          28 October); gone once all are booked, back if one is cancelled. */}
      {times.status === 'ready' && times.times.teams.length > 0 ? (
        <Link className="hour-row team-notice" to="/booking/assessments">
          <span className="hour-row-text">
            <span className="name">Find your Team's dedicated assessment times</span>
            <span className="detail">{times.times.teams.map((team) => team.name).join(', ')}</span>
          </span>
          <span className="chevron" aria-hidden="true">
            ›
          </span>
        </Link>
      ) : null}

      {clinicFirst ? clinicPanel : null}

      {nextClinic && !next ? null : (
      <section className="panel">
        <h2>Next lesson</h2>

        {state.status === 'loading' ? <WaitNote className="empty">Checking…</WaitNote> : null}

        {/* A read that failed is this app failing, and must not be reported as a
            fact about their lessons — the mistake the slot screen made. */}
        {state.status === 'failed' ? <p className="empty">{state.message}</p> : null}

        {state.status === 'ready' && !next ? (
          <p className="empty">
            {/* "Book a lesson", not "Pick a time": picking a time is one step of
                booking (Matthew, 25 October). The Book tab's page. */}
            Nothing booked. <Link className="text-link" to="/booking">Book a lesson</Link>
          </p>
        ) : null}

        {next ? (
          <Link className="hour-row" to={lessonLink(next)}>
            <span className="hour-row-text">
              <span className="name">{when(next.slot_start)}</span>
              <span className="detail">
                {[who(next), next.product_name, `Coach ${next.coach_name}`]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <PlanLines lesson={next} />
            </span>
            <span className="chevron" aria-hidden="true">
              ›
            </span>
          </Link>
        ) : null}
      </section>
      )}

      {clinicFirst ? null : clinicPanel}

      {people.status === 'ready' ? <HomeTeams people={people.people} /> : null}

      {people.status === 'ready' ? (
        <YourAthletes people={people.people} lessons={state.status === 'ready' ? state.lessons : []} />
      ) : null}

      {state.status === 'ready' ? <LatestLessons lessons={state.lessons} /> : null}

      {/* No Recul row for parents: taken off their Home by Matthew, 27 October
          (it had been a row here since board 8, 25 October). */}
    </>
  );
}

/**
 * The soonest lesson still to come.
 *
 * Cancelled ones are left out: a cancelled lesson belongs in the list, where
 * the row says it was cancelled, but it is not what happens next.
 */
function nextUp(lessons: BookedLesson[]): BookedLesson | undefined {
  const now = Date.now();
  return lessons
    .filter((lesson) => outcomeOf(lesson) === 'Booked')
    .filter((lesson) => new Date(lesson.slot_start).getTime() > now)
    .sort((a, b) => new Date(a.slot_start).getTime() - new Date(b.slot_start).getTime())[0];
}

/**
 * The plan's first line under the next lesson (ruled 13 October): the plan is
 * what that lesson will do, so it is read before it. One line per child going
 * who has a plan, named only when more than one is going. Nothing when the
 * coach has not written one yet.
 */
function PlanLines({ lesson }: { lesson: BookedLesson }) {
  const going = whoIsGoing(lesson);
  const lines = going
    .map((attendee) => ({
      attendee,
      plan: attendee.practice_plan ? firstLine(attendee.practice_plan) : '',
    }))
    .filter((line) => line.plan);

  return (
    <>
      {lines.map(({ attendee, plan }) => (
        <span key={attendee.client_id} className="sub-line excerpt">
          {going.length > 1 ? `${attendee.name}’s plan` : 'Plan'}: {plan}
        </span>
      ))}
    </>
  );
}

/**
 * Each person's most recent lesson that took place, newest first. A no-show
 * or a cancellation did not take place, so it is not anyone's latest lesson.
 */
function LatestLessons({ lessons }: { lessons: BookedLesson[] }) {
  const latest = new Map<string, { lesson: BookedLesson; attendee: Attendee }>();
  for (const lesson of lessons) {
    for (const attendee of lesson.attendees) {
      if (!attendee.lesson_id || attendeeOutcome(lesson, attendee) !== 'Completed') continue;
      const held = latest.get(attendee.client_id);
      if (!held || Date.parse(held.lesson.slot_start) < Date.parse(lesson.slot_start)) {
        latest.set(attendee.client_id, { lesson, attendee });
      }
    }
  }

  const rows = [...latest.values()].sort(
    (a, b) =>
      Date.parse(b.lesson.slot_start) - Date.parse(a.lesson.slot_start) ||
      a.attendee.name.localeCompare(b.attendee.name),
  );
  if (rows.length === 0) return null;

  return (
    <>
      <span className="section-label">Last lesson</span>
      {rows.map(({ lesson, attendee }) => {
        const plan = attendee.practice_plan ? firstLine(attendee.practice_plan) : '';
        return (
          <Link
            key={attendee.client_id}
            className="hour-row"
            to={athleteLessonLink(lesson.booking_id, attendee.lesson_id ?? '', true)}
          >
            <span className="hour-row-text">
              <span className="name">{attendee.name}</span>
              <span className="detail">
                {[day(lesson.slot_start), lesson.product_name].filter(Boolean).join(' · ')}
              </span>
              {plan ? <span className="sub-line excerpt">Plan: {plan}</span> : null}
            </span>
            <span className="outcome completed">Completed</span>
            <span className="chevron" aria-hidden="true">
              ›
            </span>
          </Link>
        );
      })}
    </>
  );
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Names, never a count — design-principles.md §4. */
function who(lesson: BookedLesson): string {
  return whoIsGoing(lesson).map((attendee) => attendee.name).join(' and ');
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
